import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TRANSLOCO_LOADER, TranslocoService } from '@ngneat/transloco';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { DEFAULT_DELIMITER_TYPE, getAccessTokenPayloadInfo, SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { BehaviorSubject, Observable, Subject, catchError, filter, finalize, forkJoin, map, merge, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { AWS_DEFAULT_BACKGROUND_IMAGE, AWS_DEFAULT_LOGO, AWS_DEFAULT_LOGO_NG } from '../../../Constants/AWS-IMAGES';
import { SettingsService } from '../../settings/services/settings.service';
import { IUserAvatar } from '../models/user-avatar';
import { UserHttpClient } from '../user-http-client';
import { User, UserEnvironment } from './user.types';
import { EnvironmentInfo } from '../../../auth/model/environment.info.model';
import { AuthStatus } from '../../../auth/model/user-auth.model';
import { LanguagesService } from '../../languages/services/languages.service';
import { SvcDataCacheService } from '../../../services/cache/svc-data-cache.service';
import { ParameterService } from '../../../services/parameter/parameter.service';
import { SvcFeatureToggleService } from '../../../services/svc-feature-toggle/svc-feature-toggle.service';
import { SvcCacheName } from '../../../services/cache/svc-cache-name.enum';
import { AuthService } from '../../../auth/auth.service';
import { UserInfo } from './user-info.model';
import { UserPreferencesService } from './user-preferences.service';
import { setRumVersion } from '../../../rum/rum-client';
import { TranslocoApiLoader } from '../../transloco/transloco.api-loader';
import { getAccessTokenSessionKey } from '../../../auth/access-token-session.util';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _userInfos$ = new BehaviorSubject<UserInfo>(null);
  public userInfos$ = this._userInfos$.asObservable();

  public user$ = this._userInfos$.pipe(map((ui) => ui?.session));

  public isGamified$ = this._userInfos$.pipe(map((ui) => ui?.isGamefied?.isGamefied));

  private _version$ = new BehaviorSubject<string>(null);
  public version$ = this._version$.asObservable();

  private _versionPendingRequest: Subject<void>;
  private _getUserInFlight$: Observable<User> | null = null;
  private _sessionBootstrapReady = false;
  private _userInfosSession: string | null = null;

  private set _environmentCached(value: EnvironmentInfo) {
    this._dataCacheService.set(SvcCacheName.ENVIRONMENT_INFO, value, { hours: 1 });
  }
  private get _environmentCached(): EnvironmentInfo {
    return this._dataCacheService.get(SvcCacheName.ENVIRONMENT_INFO);
  }

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _appConfig: AppEnvironmentConfig,
    private _authService: AuthService,
    private _translocoService: TranslocoService,
    private _settingsService: SettingsService,
    private _userHttpClient: UserHttpClient,
    private _svcAppSettgins: SvcAppSettings,
    private _featureToggleService: SvcFeatureToggleService,
    private _parameterService: ParameterService,
    private _languagesService: LanguagesService,
    private _dataCacheService: SvcDataCacheService,
    private _userPreferencesService: UserPreferencesService,
    @Inject(TRANSLOCO_LOADER) private _translocoTermsService: TranslocoApiLoader
  ) {
    this._authService.getAuthStatusFn = () =>
      this.get({
        justUserInfo: true,
      }).pipe(
        map(() => this.userInfos?.status?.status),
        // AB#72477 - a rejected user has no session (see #fetchUser), so get() rejects
        // instead of resolving. Recover the status here rather than in #fetchUser/get()
        // so every other caller keeps requiring a full session as before.
        catchError((error: Error & { authStatus?: number }) =>
          error?.authStatus === AuthStatus.RejectedUser ? of(AuthStatus.RejectedUser) : throwError(() => error)
        )
      );
    // AB#73644 - AuthService.signIn() loads the user through getAuthStatusFn before emitting signIn$. Resetting a user
    // already bootstrapped under that same token made the sign-in screen fetch it all again (user infos, feature
    // toggles, languages, dictionary); a user loaded under another token is still reset.
    merge(this._authService.signIn$.pipe(filter(() => !this.#isBootstrappedForCurrentToken())), this._authService.signOut$)
      .pipe(
        tap(() => {
          this._userInfos$.next(null);
          this._sessionBootstrapReady = false;
          this._getUserInFlight$ = null;
          this._featureToggleService.reset();
          this._userPreferencesService.reset();
          this._translocoTermsService.reset();
          this._languagesService.reset();
        })
      )
      .subscribe();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  private set userInfos(value: UserInfo) {
    this._userInfos$.next(value);
  }
  get userInfos(): UserInfo {
    return this._userInfos$.value;
  }

  set user(value: User) {
    this.userInfos = {
      ...(this._userInfos$.value ?? {}),
      session: value,
    };
  }
  get user(): User {
    return this._userInfos$.value?.session;
  }

  set isGamified(value: boolean) {
    this.userInfos = {
      ...(this._userInfos$.value ?? {}),
      isGamefied: {
        isGamefied: value,
      },
    };
  }
  get isGamified(): boolean {
    return this._userInfos$.value?.isGamefied?.isGamefied;
  }

  get version(): string {
    return this._version$.value;
  }

  get hasSubordinate(): boolean {
    return this._userInfos$.value?.hasSubortinates?.hasSubortinates;
  }

  get environment(): UserEnvironment {
    return {
      backgroundImage: this._environmentCached?.backgroundImage || AWS_DEFAULT_BACKGROUND_IMAGE,
      logoImageNegative: this._environmentCached?.logoImageNegative || AWS_DEFAULT_LOGO_NG,
      logoImage: this._environmentCached?.logoImage || AWS_DEFAULT_LOGO,
      solvaceLogoNegative: AWS_DEFAULT_LOGO_NG,
      colorBase: this._environmentCached?.colorBase,
      url: this._environmentCached?.url,
    };
  }

  get userId$(): string {
    return getAccessTokenPayloadInfo('UserId')?.toLocaleLowerCase();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get the current logged in user data
   */
  get(options?: { justUserInfo?: boolean; forceFeaturesToggle?: boolean }): Observable<User> {
    const userInfo = this._userInfos$.value;
    const hasCachedUser = this.#hasCachedUser(userInfo);
    const justUserInfo = options?.justUserInfo ?? false;

    if (this._sessionBootstrapReady && hasCachedUser && justUserInfo) {
      return of(userInfo.session);
    }

    if (this._getUserInFlight$) {
      return this._getUserInFlight$;
    }

    this._getUserInFlight$ = this.#fetchUser().pipe(
      shareReplay(1),
      finalize(() => {
        this._getUserInFlight$ = null;
      })
    );

    return this._getUserInFlight$;
  }

  #isBootstrappedForCurrentToken(): boolean {
    const session = getAccessTokenSessionKey();
    return this._sessionBootstrapReady && !!session && this._userInfosSession === session;
  }

  #hasCachedUser(userInfo: UserInfo): boolean {
    return !!userInfo?.status && (!!userInfo.session || userInfo.status.status === AuthStatus.RejectedUser);
  }

  invalidateCachedStatus(): void {
    this._userInfos$.next(null);
    this._getUserInFlight$ = null;
  }

  #fetchUser(): Observable<User> {
    const userInfo = this._userInfos$.value;
    const hasCachedUser = this.#hasCachedUser(userInfo);

    const userInfo$ = (
      hasCachedUser
        ? of(userInfo)
        : this._httpClient
            .get<UserInfo>(`${this._appConfig.APIs.apiUrlUsers}/user/infos/cached`)
            .pipe(tap(() => (this._userInfosSession = getAccessTokenSessionKey())))
    ).pipe(
      switchMap((loadedUserInfo) => {
        if (!loadedUserInfo?.session) {
          // AB#72477 - a rejected user has no session/area on purpose (see
          // UpdateRejectUserCommandHandler); attach the known status so callers that only
          // need the status (getAuthStatusFn below) can recover it instead of failing.
          if (loadedUserInfo?.status?.status === AuthStatus.RejectedUser) {
            this.userInfos = loadedUserInfo;
          }
          const sessionError = new Error('User session is unavailable.') as Error & { authStatus?: number };
          sessionError.authStatus = loadedUserInfo?.status?.status;
          return throwError(() => sessionError);
        }
        return of(loadedUserInfo);
      }),
      switchMap((loadedUserInfo) => {
        this._parameterService.setCurrentSiteId(loadedUserInfo.session.lastSiteId);
        return this._featureToggleService.load().pipe(map(() => loadedUserInfo));
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && [400, 401].includes(error.status)) {
          // AB#72477 - keep the backend's field-level errors (e.g. errors.AreaId when the
          // user's assigned area was deactivated) so callers can tell this apart from an
          // actual invalid-credentials failure instead of showing the same generic message.
          const sessionError = new Error(error.message || 'User session is unavailable.') as Error & { errors?: Record<string, string[]> };
          sessionError.errors = error.error?.errors;
          return throwError(() => sessionError);
        }
        return throwError(() => error);
      })
    );

    this._languagesService.applyAvailableLangsTo(this._translocoService);
    this._languagesService.clearStaleTranslations(this._translocoService, !this._sessionBootstrapReady);

    const languages$ = this._languagesService.getAll().pipe(
      tap((langs) => this._languagesService.applyAvailableLangsTo(this._translocoService, langs))
    );

    return forkJoin({
      userInfo: userInfo$,
      languages: languages$,
    }).pipe(
      switchMap(({ userInfo: loadedUserInfo }) => {
        const lang = loadedUserInfo.session.languageTag;
        this._translocoService.setActiveLang(lang);
        return this._translocoService.load(lang).pipe(map(() => loadedUserInfo));
      }),
      map((loadedUserInfo) => this.#mapLoadedUserInfo(loadedUserInfo))
    );
  }

  #mapLoadedUserInfo(loadedUserInfo: UserInfo): User {
    if (!loadedUserInfo?.session || !loadedUserInfo?.status) {
      throw new Error('User session is unavailable.');
    }

    const { session, status, currency } = loadedUserInfo;

    if (this._authService.userStatusAuth) {
      this._authService.userStatusAuth.status = status.status;
    }

    this._parameterService.setCurrentSiteId(session.lastSiteId);
    this._svcAppSettgins.setDateFormat(session.dateFormat);
    this._svcAppSettgins.setDelimiterSymbol(session.delimiter);
    if (currency?.currencySymbol) {
      this._svcAppSettgins.setCurrencyAcronym(currency.currencySymbol);
    }
    this.getVersion();
    this.userInfos = loadedUserInfo;
    this._sessionBootstrapReady = true;

    return loadedUserInfo.session;
  }

  private getVersion(): void {
    if (this._versionPendingRequest || this._version$.value) return;
    this._versionPendingRequest = new Subject<void>();
    this._parameterService
      .getAllAppGlobalParams()
      .pipe(
        tap((res) => {
          this._version$.next((res.find((p) => p.key === 'VERSION_ID')?.value ?? '00000').toString());
          setRumVersion(this._version$.value);
        }),
        finalize(() => {
          this._versionPendingRequest = null;
        })
      )
      .subscribe();
  }

  getEnvironment(): Observable<UserEnvironment> {
    const fromCached = !!this._environmentCached;
    return (
      this._environmentCached
        ? of(this._environmentCached)
        : this._httpClient.post<EnvironmentInfo>(`${this._appConfig.APIs.apiUrlAuth}/Environment/info`, { environmentUrl: getAccessTokenPayloadInfo('iss') })
    ).pipe(
      switchMap((info) => {
        if (!fromCached) this._environmentCached = info;
        return this._settingsService.handleThemeChange(info.colorBase.replace('#', '').toUpperCase()).pipe(map(() => this.environment));
      })
    );
  }

  update(user: User): Observable<any> {
    return this._httpClient.put<User>(`${this._appConfig.APIs.apiUrlUsers}/UserPreference`, user);
  }

  updateCheckinFilter(payload: {
    setAsDefault: boolean;
    dontAskAgain: boolean;
    udaId: number | null;
    equipmentId: number | null;
  }): Observable<any> {
    if (payload.dontAskAgain) {
      // if user don't want to be asked again, we will set the current selection as default
      payload.setAsDefault = true; 
    }
    return this._httpClient.patch(`${this._appConfig.APIs.apiUrlUsers}/UserPreference/CheckInFilter`, payload);
  }

  updateUserImg(userImg: any): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('fileData', userImg);

    let headers = new HttpHeaders();
    /** In Angular 5, including the header Content-Type can invalidate your request */
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    let params = new HttpParams();

    const options = {
      headers: headers,
      params: params,
      reportProgress: true,
    };

    return this._httpClient.put(`${this._appConfig.APIs.apiUrlUsers}/userPreference/picture`, formData, options);
  }

  deleteImg(userImg: any) {
    return this._httpClient.delete(`${this._appConfig.APIs.apiUrlUsers}/userPreference/picture`, userImg);
  }

  getAvatar(userId: string): Observable<IUserAvatar> {
    return this._userHttpClient.get(`/User/Avatar`, {
      params: {
        userId: userId,
      },
    });
  }
  getUsers(active: boolean = true, siteId?: number) {
    const scopedToSite = siteId != null && Number.isFinite(siteId) && siteId > 0;
    return this._userHttpClient.get<
      {
        userId: string;
        name: string;
        active: boolean;
      }[]
    >(`/User/UsersAndGuests/names`, { params: scopedToSite ? { active: active, siteId: siteId } : { active: active } });
  }

  getUsersAndGuests(active: boolean = true) {
    return this._userHttpClient.get<
      {
        userId: string;
        name: string;
        active: boolean;
      }[]
    >(`/User/UsersAndGuests/names`, { params: { active: active } });
  }

  getUsersInfo(params: { pageSize: number; pageIndex: number; name?: string; active: boolean }) {
    const httpParams: Record<string, string | number | boolean> = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      active: params.active,
    };
    if (params.name) {
      httpParams['name'] = params.name;
    }
    return this._userHttpClient.get<{
      users: {
        userId: string;
        name: string;
        site: string;
        isGlobal: boolean;
      }[];
      page: {
        pageIndex: number;
        pageSize: number;
        pagesTotal: number;
        registersTotal: number;
      };
    }>('/User/Info', { params: httpParams });
  }

  getUsersInfoById(ids: string[]) {
    return this._userHttpClient.get<
      {
        userId: string;
        name: string;
        site: string;
        isGlobal: boolean;
      }[]
    >('/User/InfoByIds', {
      params: {
        userId: ids,
      },
    });
  }

  getTeams(active: boolean = true) {
    return this._userHttpClient.get<
      {
        groupId: number;
        groupName: string;
        active: boolean;
      }[]
    >('/Groups/names', {
      params: {
        active: active,
      },
    });
  }

  getTeamsInfo(params: { pageSize: number; pageIndex: number; name: string; active: boolean; originatorId?: string }): Observable<{
    teams: [
      {
        teamId: number;
        name: string;
        siteId: number;
        siteName: string;
      },
    ];
    page: {
      pageIndex: number;
      pageSize: number;
      pagesTotal: number;
      registersTotal: number;
    };
  }> {
    return this._userHttpClient.get('/Team', { params: { ...params } });
  }

  getTeamInfo(ids: number[]) {
    return this._userHttpClient.get('/Team/InfoByIds', { params: { teamId: ids } });
  }

  getArea(active: boolean = true) {
    return this._httpClient.get<any[]>(`${this._appConfig.APIs.apiUrlUsers}/Area/names`, { params: { active: active } });
  }

  getEmployeesSite() {
    return this._httpClient.get<any[]>(`${this._appConfig.APIs.apiUrlUsers}/User/UsersAndGuests/names`, { params: { active: true } });
  }

  getEmployeesAreas(areaIds: number[]) {
    let params = new HttpParams();
    areaIds.forEach((id) => {
      params = params.append('areaIds', id.toString());
    });
    return this._httpClient.get<any[]>(`${this._appConfig.APIs.apiUrlUsers}/User/names/areas`, { params });
  }

  getEmployeesTeams(teamIds: number[]) {
    let params = new HttpParams();
    teamIds.forEach((id) => {
      params = params.append('teamIds', id.toString());
    });
    return this._httpClient.get<any[]>(`${this._appConfig.APIs.apiUrlUsers}/User/names/teams`, { params });
  }

  getSubordinateNames(leaderUserId: string) {
    return this._userHttpClient.get<
      {
        userId: string;
        name: string;
        active: boolean;
      }[]
    >(`/User/${leaderUserId}/subordinate/names`);
  }

  getEmployeesByLeaders(leaderIds: string[]) {
    if (!leaderIds?.length) {
      return of([]);
    }

    return forkJoin(leaderIds.map((leaderId) => this.getSubordinateNames(leaderId))).pipe(
      map((lists) => {
        const byId = new Map<string, { userId: string; name: string; active?: boolean }>();
        lists.flat().forEach((user) => {
          if (user?.userId && !byId.has(user.userId)) {
            byId.set(user.userId, user);
          }
        });
        return Array.from(byId.values());
      })
    );
  }
}
